import { useLazyQuery } from '@apollo/client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Flex, FloraButton } from '@grupoboticario/flora-react'
import { MagnifyingGlassIcon } from '@grupoboticario/flora-react-icons'

import { AlertMessage } from '@/components/SearchInput/AlertMessage'
import { SearchInput } from '@/components/SearchInput/SearchInput'
import { useUserContext } from '@/context/user'
import { GET_MEMBER_BY_DOCUMENT_NUMBER } from '@/graphql/queries/memberByDocumentNumber'
import { PlusCircle } from '@/icons/PlusCircle'
import { Actions as PermissionActions } from '@/utils/dictionaries/actionsDictionary/actions'
import { getMemberSearchConfig } from '@/utils/dictionaries/memberSearchDictionary/memberSearchDictionary'
import { isBrowser } from '@/utils/isBrowser'
import { businessUnit } from '@/utils/themes/theme'

import * as P from '../pages.styles'

interface BoxMessage {
  message: string
  type: 'error' | 'info'
}

const UserPage = () => {
  const businessUnitValue = isBrowser() ? businessUnit(globalThis.location.hostname) : null
  const config = getMemberSearchConfig(businessUnitValue)
  const { notFoundMessage, errorMessage } = config

  const { hasPermission } = useUserContext()
  const navigate = useNavigate()

  const [boxMessage, setBoxMessage] = useState<BoxMessage>({ message: config.initialMessage, type: 'info' })

  const [searchMember, { data, loading, error }] = useLazyQuery(GET_MEMBER_BY_DOCUMENT_NUMBER, {
    fetchPolicy: 'network-only'
  })

  const handleSubmit = (value: string) => {
    const rawValue = value.replaceAll(/\D/g, '')
    if (!rawValue.trim()) {
      setBoxMessage({ message: config.emptyMessage, type: 'error' })
      return
    }
    setBoxMessage({ message: config.initialMessage, type: 'info' })
    searchMember({ variables: { documentNumber: rawValue } })
  }

  useEffect(() => {
    if (error) {
      setBoxMessage({ message: errorMessage, type: 'error' })
      return
    }
    if (!data) return
    if (data.member?.documentNumber) navigate(`/members/${data.member.documentNumber}`)
    else setBoxMessage({ message: notFoundMessage, type: 'error' })
  }, [data, error, navigate, notFoundMessage, errorMessage])

  return (
    <Box css={P.pageWrapperCss}>
      <SearchInput
        buttonLabel={config.buttonLabel}
        formatValue={config.formatValue}
        leftIcon={<MagnifyingGlassIcon />}
        loading={loading}
        onSubmit={handleSubmit}
        placeholder={config.placeholder}
      />
      <Flex justify="end" wrap="wrap">
        <FloraButton
          disabled={!hasPermission(PermissionActions.CREATE_USER)}
          isUppercase
          has="iconRight"
          icon={<PlusCircle />}
          onClick={() => navigate('/members/new')}
          hierarchy="tertiary"
        >
          Novo participante
        </FloraButton>
      </Flex>
      {boxMessage && <AlertMessage message={boxMessage.message} type={boxMessage.type} />}
    </Box>
  )
}

export default UserPage

export { UserPage as MembersPage }
